import { Link, MenuItem, SvgIcon, Typography } from '@mui/material';
import { ChevronRight } from '@opengov/react-capital-assets';
import React from 'react';

import { FavoriteStarButton } from '../../Components/FavoriteStarButton';
import { NAV_COLOR_PALETTE } from '../../Components/styleConstants';
import { useMenuContext } from '../../ContextState/useMenuContext';
import { DATA_TEST } from '../../enums';
import { IMenuItem } from '../../types';
import { itemIsLeafNode } from '../../utils/utils';

const textStyles = {
  backgroundColor: 'inherit',
  textAlign: 'center',
  color: `${NAV_COLOR_PALETTE.primaryGray}`
};
const menuItemStyles = {
  px: 4,
  paddingRight: 3,
  overflow: 'hidden',
  justifyContent: 'space-between',
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 0,
  maxHeight: 40
};

interface IMobileMenuItemProps {
  item: IMenuItem;
  dataTest: string;
  dataOG?: string;
  showIcon?: boolean;
  autoFocus?: boolean;
  firstTrayMenuItemRef?: React.MutableRefObject<HTMLLIElement>;
  handleClick(item?: IMenuItem): void;
}
export const MobileMenuItem: React.FC<IMobileMenuItemProps> = ({
  item,
  handleClick,
  dataTest,
  dataOG,
  showIcon = true,
  autoFocus = false,
  firstTrayMenuItemRef
}) => {
  const { onFavoriteIconClick, favorites, currentLevelOneItem, disableFavorites } = useMenuContext();
  const isLeafNode = itemIsLeafNode(item);
  const [favoritedItem, setFavoritedItem] = React.useState<IMenuItem | null>(null);
  const linkRef = React.useRef<HTMLAnchorElement>(null);
  React.useEffect(() => {
    const matchingLevelOneFavorites = favorites?.find((fav) => fav?.id === currentLevelOneItem?.id);
    if (matchingLevelOneFavorites) {
      const isFavorited = matchingLevelOneFavorites?.items.find((fav) => fav.id === item.id);
      setFavoritedItem(isFavorited ?? null);
    } else {
      setFavoritedItem(null);
    }
  }, [favorites, currentLevelOneItem]);
  return (
    <MenuItem
      ref={firstTrayMenuItemRef}
      autoFocus={autoFocus}
      data-test={dataTest}
      data-og={dataOG}
      onClick={(e) => {
        handleClick(item);
        if (item.url) {
          // If we didn't click directly on the url, click inner Link component
          // (also important for keyboard navigation & general accessibility)
          if (e.target !== linkRef?.current) {
            e.preventDefault();
            linkRef?.current?.click();
          }
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Prevent from bubbling up and triggering handleClose on PopperSubmenuContainer
          e.stopPropagation();
        }
        if (e.key === 'ArrowRight' && !isLeafNode) {
          handleClick(item);
        }
      }}
      sx={menuItemStyles}
      disableRipple={true}
    >
      {item.url ? (
        <Link
          href={item.url}
          target={item?.openInNewTab ? '_blank' : undefined}
          underline="none"
          component={item.linkComponent || Link}
          data-test={DATA_TEST.mobile_menu_item_link}
          sx={{
            ...textStyles,
            ':visited': {
              color: `${NAV_COLOR_PALETTE.primaryGray}`
            }
          }}
          noWrap={true}
          ref={linkRef}
          tabIndex={-1}
        >
          <Typography variant="bodyDefault" sx={textStyles} component="span" noWrap={true}>
            {item.label}
          </Typography>
        </Link>
      ) : (
        <Typography
          variant="bodyDefault"
          data-test={DATA_TEST.mobile_menu_item_label}
          sx={textStyles}
          component="span"
          noWrap={true}
        >
          {item.label}
        </Typography>
      )}
      {showIcon && !isLeafNode && (
        <SvgIcon
          data-test={DATA_TEST.chevron_right_icon}
          inheritViewBox
          component={ChevronRight}
          sx={{ color: NAV_COLOR_PALETTE.primaryGray }}
        />
      )}
      {showIcon && isLeafNode && !disableFavorites && (
        <FavoriteStarButton
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            await onFavoriteIconClick(Boolean(favoritedItem), item);
          }}
          favorited={Boolean(favoritedItem)}
        />
      )}
    </MenuItem>
  );
};
